import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AppInjector } from '../../app-injector';
import { ProgressIndicatorPopupComponent } from '../../shared/components/progress-indicator-popup/progress-indicator-popup.component';
import { MiVoiceBusinessMultiEditDialogComponent } from '../../modules/users-multi-edit/components/user-multi-edit-dialogs/mivoice-business-multi-edit-dialog/mivoice-business-multi-edit-dialog.component';
import { mivoiceBusinessLicenses } from '../constants/constants';
import { CustomValidators } from '../utils/CustomValidators';
import { UserBulkProduct, UserBulkProductItemServiceInf } from '../interfaces/user-prod-bulk-edit-interfaces';
import { Util } from '../utils/Util';
import { AdminService } from './admin.service';
import { OrdersService } from './orders.service';
import { UserMiVBSharedService } from './user-mivb-shared.service';
import { UserProdMiVBService } from './user-prod-mivb.service';
import { IntegCustomerAdminPortalService } from './integ-customer-admin-portal.service';

@Injectable({
  providedIn: 'root'
})
export class UsersBulkEditMiVBService implements UserBulkProductItemServiceInf{
  private adminService: AdminService;
  private userMiVBSvc: UserProdMiVBService;
  private translateService: TranslateService;
  private modalService: NgbModal;
  private mivbSharedSvc: UserMiVBSharedService;
  private ordersService: OrdersService;
  private capService: IntegCustomerAdminPortalService;

  constructor() { }

  initializeServices() {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.userMiVBSvc)
      this.userMiVBSvc = AppInjector.injector.get(UserProdMiVBService);

    if (!this.translateService)
      this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.mivbSharedSvc)
      this.mivbSharedSvc = AppInjector.injector.get(UserMiVBSharedService);

    if (!this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);

    if (!this.modalService)
      this.modalService = AppInjector.injector.get(NgbModal);

    if (!this.capService)
      this.capService = AppInjector.injector.get(IntegCustomerAdminPortalService);
  }

  shouldBeDisplayed(accountId: string): Promise<boolean> 
  {
    this.initializeServices();

    let promises = [];
    let mivbSubTest = this.mivbSharedSvc.getRemainingLicenseDetails().then( licenses => 
      {
          return Object.keys(licenses).length > 0;
      });
    promises.push(mivbSubTest);

    let integrationPromise = this.adminService.getAccountTags(accountId).then( tags => 
      {
          if (tags && tags.products && tags.products.length > 0 && tags.products.includes("MiVB")) 
          {
            return true;
          }
          return false;
        })
        .catch(err => {
          console.log("There was a problem fetching MiVoice Business related account tags", err);
          return false;
      });
    promises.push(integrationPromise); 

    let capPromise = this.capService.isCAPFeatureEnabled({accountId: accountId} as any).then( enabled => {
      return !enabled;
    });
    promises.push(capPromise);

    return Promise.all(promises).then( values => {
      return values[0] && values[1] && values[2];
    });            
  }

  getRemainingLicenses(accountId: string): Promise<any> {
    this.initializeServices();
    return this.userMiVBSvc.getRemainingLicenses({accountId}).then( value => {
          let result = {
            "mivb": value
          };
          return result;
      });
  }

  getBulkEditDialog(accountId: string, userBulkProd: UserBulkProduct): Promise<any> {
    return Promise.resolve(MiVoiceBusinessMultiEditDialogComponent);
  }

  setProgressDataTitleInformation(progressData: any, tasks: any, currentTask: string, source?:string) {
      if(currentTask === 'license') {
        progressData.title = 'product_licenses.mivb.MiVB_license_config.name';
        progressData.actionString = 'users_multi_edit.mivoiceBusiness.' + (tasks[currentTask] === "unassignAll" ? 'unassigning_license_action': 'assigning_license_action')
      }
  }

  async saveUserMiVoiceBusinessBulkOperation(accountId: string, users: User[], values: any, source?:string) {
    this.initializeServices();
    let changes = Object.keys(values);
    let currentUsersList = [...users];
    try
		{
			console.log("Going to assign/unassign licenses to specified users ", users.length)

			let progressUpdate = new Subject();

			let progressData = {
        total: 0,
        done: 0,
        step: undefined,
        errorMessage: '',
        successMessage: undefined,
        updateSubject: progressUpdate
			}
			let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
				backdrop: 'static',
				keyboard: false,
				centered: true,
			});

			modal.componentInstance.progressData = progressData;
      
      for (let i = 0; i < changes.length; i++) {
        if (changes[i] === "license") {
          await this.updateMiVoiceBusinessLicenseForUsers(accountId, currentUsersList, values, progressData);
        }
      }

      progressData.successMessage = this.translateService.instant("users_multi_edit.process_complete" );
			progressUpdate.next( progressData );
			return modal.result.then(result => {
				modal = null;
				progressUpdate = null;
				return;
			});
    } catch(err) {
      console.log("There was an error for saving mitel one", err);
    }
  }

  async updateMiVoiceBusinessLicenseForUsers( accountId: string, users: User[], values: any, progressData: any ) 
  {
    this.setProgressDataTitleInformation(progressData, values, "license");
    progressData.total = users.length;
    progressData.done = 0;
    progressData.updateSubject.next(progressData);
    let failedUsers = [];
    let skippedUsers = [];
    let noMoreLicenses = false;
    let remainingUsers = 0;
    if ( values["license"] === "unassignAll" ) 
    {
        for ( let i = 0; i < users.length; i++ ) 
        {
            await Util.timeout(50);
            let currentLicense = await this.mivbSharedSvc.isMiVoiceBusinessLicenseAssignedToUser(users[i]);
            let alreadyHadLicense = currentLicense != undefined ? true : false;
            if ( alreadyHadLicense )
            {
                let body: any = [];
                for ( let j = 0; j < mivoiceBusinessLicenses.length; j++ ) 
                {
                  body.push({
                    name: mivoiceBusinessLicenses[j],
                    enable: false,
                    count: 1
                  })
                }
                let removePromise = this.ordersService.addOrRemoveMultipleUserLicense(users[i].userId, body)
                    .catch(err => {
                      if ( err.statusCode == 500) {
                        // attempting to remove the licenses again
                        return Util.timeout(100).then(() => {
                          return this.ordersService.addOrRemoveMultipleUserLicense(users[i].userId, body);
                        })
                      }
                    });
                
                await removePromise.then( result => {
                  console.log(" user entitlement is removed successfully");
                  let tagBody = {
                    products: []
                  };
                  if (users[i].tags?.products) {
                    tagBody.products.push(...users[i].tags.products);
                  }
                  let ndx =  tagBody.products.indexOf('mivb');
                  if(ndx > -1)
                    tagBody.products.splice(ndx, 1);
                  return this.adminService.tryUpdateTagsOfUserByAccountId(users[i].accountId, users[i].userId, tagBody, users[i].tags? users[i].tags: {});
                }).catch( error => {
                  failedUsers.push(users[i]);
                });
            }
            progressData.done++;
			      progressData.updateSubject.next( progressData );
        }
    } 
    else 
    {
      // Assigning or changing the mivoice business tier license
        for(let i = 0; i < users.length; i++) {
            const licenseName = values["license"];
            let body: any = [{
              name: licenseName,
              enable: true,
              count: 1
            }];
            let licensesToRemove = mivoiceBusinessLicenses.filter( license => license != licenseName);
            for (let j = 0; j < licensesToRemove.length; j++) {
              body.push({
                name: licensesToRemove[j],
                enable: false,
                count: 1
              })
            }
            let alreadyHadLicense = false;
            let licenseSuccess = false;
            let currentLicense = undefined;

            try {
              await Util.timeout(50);
              let emailTest = await this.shouldAssignLicenseToUser( accountId, users[i] );
              if (!emailTest) { 
                  skippedUsers.push(users[i]);
              } else { // only eligible users can have a license
                  currentLicense = await this.mivbSharedSvc.isMiVoiceBusinessLicenseAssignedToUser(users[i]);
                  alreadyHadLicense = currentLicense != undefined ? true : false;
                  // Assigning license without checking if it already has it. In case it does then it will return 
                  // 409 which will be handled and accepted as successful operation.
                  await this.ordersService.addOrRemoveMultipleUserLicense(users[i].userId, body );
                  console.log("Update user license succeeded");
                  licenseSuccess = true;
                }
            } catch (err) {
              if ( err.statusCode == 409 && err.body && err.body.message.indexOf( "Insufficient entitlements" ) != -1 ) {
                // ran out of licenses
                noMoreLicenses = true;
                remainingUsers = users.length - i - failedUsers.length;
                progressData.done = progressData.done - 1;
                progressData.updateSubject.next( progressData );
                break;
              } else if (err.statusCode == 409 && alreadyHadLicense) {
                console.log('User already has the license which is being assigned', users[i]);
                //Not going to set licenseSuccess as true because we don't want to send the welcome email again.
              } else if (err.statusCode == 500 || err.toString().indexOf( "Failed to fetch" ) != -1) {
                  try {
                    // this error can occur if the system is too loaded. We will try again once before we declare it an error
                    await Util.timeout(100);
                    await this.ordersService.addOrRemoveMultipleUserLicense(users[i].userId, body )
                    console.log("Second attempt worked!")
                    licenseSuccess = true;
                  } catch (err2) {
                    if (err2.statusCode == 409 && alreadyHadLicense) {
                      console.log('User already has the license which is being assigned', users[i]);
                    } else {
                      //failed again. We will just handle it as an error
                      console.log("Second attempt to assign license failed too for user", users[i].userId, err2)
                      failedUsers.push(users[i]);
                    }
                  }
              }
            }

            if(licenseSuccess) { 

              // current post processing sends a welcome email 
              // only valid when the user's license goes from entry or none to Elite or Premier 
              let emailWorthyChange: boolean = false;
              if ( ((currentLicense == undefined || currentLicense == "MiVB_Entry") && 
                  (licenseName == "MiVB_Elite" || licenseName == "MiVB_Premier")) )
                  emailWorthyChange = true;

              await this.mivbSharedSvc.postLicenseChangeProcessing(users[i], emailWorthyChange)
            }
            progressData.done++;
            progressData.updateSubject.next( progressData );
        }
    }
    this.ordersService.clearAcctSubscriptionsCache();
    let feedbackMsg = '';
    let feedbackErrorMsg = '';
    let feedbackWarningMsg = '';
    if (failedUsers.length > 0 || noMoreLicenses || skippedUsers.length > 0) {
      if (noMoreLicenses) {
        progressData.errorMessage += feedbackMsg + (progressData.actionString ? this.translateService.instant(progressData.actionString) : '') + "<br>";
        let message = this.translateService.instant('users_multi_edit.license_unavailable', { remaining: remainingUsers });
        feedbackErrorMsg += feedbackMsg + message;
        progressData.errorMessage += message + "<br>";
      } else if (values["license"] === "unassignAll") {
        progressData.errorMessage += feedbackMsg + (progressData.actionString ? this.translateService.instant(progressData.actionString) : '') + "<br>";
        let message = this.translateService.instant('users_multi_edit.fail_unassign', {
          failed: failedUsers.length,
          names: this.getNamesFromUsers(failedUsers)
        });
        feedbackErrorMsg += feedbackMsg + message;
        progressData.errorMessage += message + "<br>";
      } else {
        if (skippedUsers.length > 0) {
          feedbackWarningMsg = feedbackMsg + this.translateService.instant('users_multi_edit.skip_assign', {
            skipped: skippedUsers.length,
            names: this.getNamesFromUsers(skippedUsers)
          });
        }
        if (failedUsers.length > 0) {
          progressData.errorMessage += feedbackMsg + (progressData.actionString ? this.translateService.instant(progressData.actionString) : '') + "<br>";
          let message = this.translateService.instant('users_multi_edit.fail_assign', {
            failed: failedUsers.length,
            names: this.getNamesFromUsers(failedUsers)
          });
          feedbackErrorMsg += feedbackMsg + message;
          progressData.errorMessage += message + "<br>";
        }
      }
    } else {
      feedbackMsg += this.translateService.instant('users_multi_edit.success_message');
    }
    progressData.updateSubject.next(progressData);
  }

  async shouldAssignLicenseToUser( accountId: string, user: User ): Promise<boolean> {
    if(Util.isReadOnlyMode(user.source))
		{
			// when user is from read only source type, then they are ignored in bulk license assign operation.
			return Promise.resolve(false);
		}
    if ( user.userVerified ) {
      return Promise.resolve(true); //according to Jeff, once s user is verified, no other users with the same address are synced
    }
    if ( user.email && user.email != null && user.email.length > 0 ) {
      const hasDup = await this.adminService.userHasADuplicateEmail(accountId, user);
      const validEmail = CustomValidators.userHasValidEmail(user);
      if (hasDup || !validEmail) {
        console.log("Do not assign a MiVoice Business license because user has a duplicate or invalid email", user);
        return false;
      }
      else {
        return true;
      }
    } else {
        // when user doesn't have a email field and also are not verified, then they are ignored in bulk license assign operation.
        return Promise.resolve(user.userVerified);
      }
	}

 
  getNamesFromUsers(users: User[]) {
    let names = '';
    let i;
    for( i = 0; i< users.length-1; i++) {
      names+= (users[i].displayName ? users[i].displayName: users[i].email) + ', ';
    }
    names+= users[i].displayName ? users[i].displayName: users[i].email;
    return names;
  }
}
